import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import Button from '../../lib/button/Button';
import {
	DataTypeState,
	PrimitiveTypes,
	LiteralValueState,
	toCamelCase,
	DefinitionEntityState,
	ChangeSet,
	ProjectState,
	resolveDataTypeLabel,
	EntityType,
	resolveEntityDataTypeLabel,
	resolveEntityName,
	getParentCanvasEntity,
	CANVAS_CARD_WIDTH,
	calculateCardSize,
	CANVAS_VERTICAL_BUFFER_BETWEEN_CARDS,
	createDefinitionEntityFromJSONObject,
	findEntityFromKeys,
	EntityWithValueState,
	READABLE_ENTITY_TYPES,
	TERMINATION_TYPES,
	lowercaseFirstLetter,
	resolveEntityCodeName,
	isObject,
	StateMutationAction,
	flattenDataTypeAndGroup,
	flattenDataTypesInOrGroup,
	EntityWithDefaultValueState,
	InputMapState,
	IExampleValuesOption
} from '@elyx-code/project-logic-tree';
import FormatIconBlack from '../../assets/code-formatting/format-black.svg';
import FormatIconWhite from '../../assets/code-formatting/format-white.svg';
import InlineIconBlack from '../../assets/code-formatting/inline-black.svg';
import InlineIconWhite from '../../assets/code-formatting/inline-white.svg';
import './json-editor-dialog.css';
import InitializeValueButton from '../dynamic-value/InitializeValueButton';

import { DraggableObject } from '../../lib/canvas';
import { getCanvasEntityDraggableContainerDOMId } from '../../services/editor/ui';
import {
	DynamicValueTypes,
	IValueStoreClient
} from '../../services/editor/value-store';
import { EditorService } from '../../services/editor';
import { popupNotification } from '../../popup-notification';
import RawJsonEditor from '../../lib/json-editor/RawJsonEditor';
import {
	FormControl,
	IconButton,
	InputLabel,
	Menu,
	MenuItem,
	OutlinedInput,
	Select,
	Tooltip,
	ToggleButton,
	ToggleButtonGroup
} from '@mui/material';
import CheckboxWithLabel from '../../lib/checkbox/CheckboxWithLabel';
import {
	castHTMLInputValueToType,
	resolveInputTypeFromDataType
} from '../../services/editor/utils';
import DropdownMenuPopup from '../../lib/dropdown/DropdownMenuPopup';
import JSONEditor, {
	ExpandOptions,
	ParseError,
	SchemaValidationError
} from 'jsoneditor';
import { JSONSchema7 } from 'json-schema';
import { handleLiteralValueChange } from '../../services/execution/value-resolution';
import { entitiesToUniqueCanvasEntities } from '../../services/editor/utils';
import { Logger } from '@elyx-code/common-ts-utils';
import DataTypeIcon, {
	ExplicitDataTypeIcon
} from '../draggable-entity-card/DataTypeIcon';
import DefinitionEntityIcon from '../draggable-entity-card/definition-entity/DefinitionEntityIcon';
import { resolveCardSubheader } from '../../services/editor/ui';
import {
	fromLiteralValueTypeToFontColor,
	fromLiteralValueTypeToExplicitIconName,
	fromLiteralValueTypeToReadableLabel,
	resolveLiteralValueSubheader
} from '../data-type/DataTypeBuilder';

interface IJsonEditorDialogProps {
	onClose?: (event: React.MouseEvent | null) => void;
	valueOwner: EntityWithValueState;
	value?: LiteralValueState;
	valueType?: DynamicValueTypes;
	draggableObject?: DraggableObject;
	project: EditorService;
	onlyDefault?: boolean;
	embedded?: boolean;
	onChange?: (newVal: any) => void;
}

function toJSONSchema(dataType: DataTypeState, title?: string): JSONSchema7 {
	let jsonSchema: JSONSchema7 = {
		// "$schema": "https://json-schema.org/draft-07/schema#",
		title:
			title || resolveDataTypeLabel(dataType) || 'Unspecified data type'
	};

	if (!dataType) {
		return jsonSchema;
	}

	let nestedSchemaUnderList = false;

	if (dataType.isList) {
		jsonSchema.type = 'array';
		nestedSchemaUnderList = true;
	}

	if (
		!dataType.entity &&
		!dataType.andChildrenGroup?.length &&
		!dataType.orChildrenGroup?.length &&
		!dataType.foreignKeyRef
	) {
		return jsonSchema;
	}

	let remainingSchema: JSONSchema7 = {};

	if (dataType.foreignKeyRef) {
		const dataTypeOfReferencedProperty =
			dataType.foreignKeyRef.getDataType(null);
		if (dataTypeOfReferencedProperty) {
			remainingSchema = toJSONSchema(dataTypeOfReferencedProperty);
			delete remainingSchema.title;
		}
	} else if (dataType.entity) {
		if (dataType.entity.type === EntityType.PrimitiveEntity) {
			switch (dataType.entity.name) {
				case PrimitiveTypes.Boolean:
					remainingSchema.type = 'boolean';
					break;
				case PrimitiveTypes.Number:
					remainingSchema.type = 'number';
					break;
				case PrimitiveTypes.String:
					remainingSchema.type = 'string';
					break;
				case PrimitiveTypes.KeyValue:
					remainingSchema.type = 'object';
					break;
				case PrimitiveTypes.Untyped:
					delete remainingSchema.type;
					break;
				case PrimitiveTypes.Null:
					remainingSchema.type = 'null';
					break;
				case PrimitiveTypes.Date:
					remainingSchema.type = 'string';
					remainingSchema.format = 'date-time';
					break;
				case PrimitiveTypes.Enum:
					remainingSchema.type = 'string';
					remainingSchema.enum = dataType.options;
					break;
				case PrimitiveTypes.UUID:
					remainingSchema.type = 'string';
					remainingSchema.format = 'uuid';
					break;
				case PrimitiveTypes.FileReference:
					remainingSchema.type = 'string';
					remainingSchema.format = 'file';
					break;
			}
		} else if (dataType.entity.type === EntityType.DefinitionEntity) {
			if (dataType.asType) {
				remainingSchema.type = 'object';
				// Allow any other properties for now
				remainingSchema.properties = {
					id: { type: 'string' },
					name: { type: 'string' },
					// Literal string value
					type: {
						type: 'string',
						enum: [EntityType.DefinitionEntity]
					}
				};
				remainingSchema.additionalProperties = true;
				remainingSchema.required = ['id', 'name', 'type'];
			} else {
				remainingSchema.type = 'object';
				remainingSchema.properties = {};
				remainingSchema.additionalProperties = false;
				dataType.entity.properties.forEach((property) => {
					const propertyDataType = property.getDataType(null);
					const entityName = resolveEntityName(
						property,
						dataType.project
					);

					const resolveCodeName =
						property.codeName ||
						(resolveEntityCodeName(
							property,
							dataType.project
						) as string) ||
						lowercaseFirstLetter(toCamelCase(entityName));

					remainingSchema.properties[resolveCodeName] =
						toJSONSchema(propertyDataType);

					// Because of how the sytem works, if the property is not required, we allow null values
					if (!property.required) {
						const existingPropertySchema = remainingSchema
							.properties[resolveCodeName] as JSONSchema7;

						if (existingPropertySchema) {
							if (Array.isArray(existingPropertySchema.type)) {
								// If it is already an array, we add null if not present
								if (
									!existingPropertySchema.type.includes(
										'null'
									)
								) {
									existingPropertySchema.type.push('null');
								}
							} else if (
								typeof existingPropertySchema.type === 'string'
							) {
								// If it is a string, we convert to array
								if (existingPropertySchema.type !== 'null') {
									existingPropertySchema.type = [
										existingPropertySchema.type,
										'null'
									];
								}
							}
						}
					}
				});

				// Add required properties
				remainingSchema.required = [];
				dataType.entity.properties.forEach((property) => {
					if (property.required) {
						const entityName = resolveEntityName(
							property,
							dataType.project
						);

						const resolveCodeName =
							property.codeName ||
							(resolveEntityCodeName(
								property,
								dataType.project
							) as string) ||
							lowercaseFirstLetter(toCamelCase(entityName));

						remainingSchema.required.push(resolveCodeName);
					}
				});
			}
		} else if (dataType.entity.type === EntityType.ActionDescriptor) {
			remainingSchema.type = 'object';
			// Allow any other properties for now
			remainingSchema.properties = {
				id: { type: 'string' },
				name: { type: 'string' },
				// Literal string value
				type: {
					type: 'string',
					enum: [EntityType.ActionDescriptor]
				}
			};
			remainingSchema.additionalProperties = true;
			remainingSchema.required = ['id', 'name', 'type'];
		}
	} else if (!!dataType.andChildrenGroup?.length) {
		remainingSchema.allOf = dataType.andChildrenGroup.map((child) =>
			toJSONSchema(child)
		);
	} else if (!!dataType.orChildrenGroup?.length) {
		remainingSchema.anyOf = dataType.orChildrenGroup.map((child) =>
			toJSONSchema(child)
		);
	}

	if (nestedSchemaUnderList) {
		jsonSchema.items = remainingSchema;
	} else {
		jsonSchema = {
			...jsonSchema,
			...remainingSchema
		};
	}

	return jsonSchema;
}

function getOptionIcon(opt: IExampleValuesOption): string {
	const keyLower = (opt.key || '').toLowerCase();
	const labelLower = (opt.label || '').toLowerCase();

	if (keyLower.includes('default') || labelLower.includes('default')) {
		return 'fa-solid fa-wand-magic-sparkles';
	}
	if (
		keyLower.includes('empty') ||
		labelLower.includes('empty') ||
		keyLower.includes('minimal')
	) {
		return 'fa-solid fa-square-minus';
	}
	if (
		keyLower.includes('full') ||
		labelLower.includes('full') ||
		keyLower.includes('complete')
	) {
		return 'fa-solid fa-cubes';
	}
	if (
		keyLower.includes('sample') ||
		labelLower.includes('sample') ||
		keyLower.includes('example')
	) {
		return 'fa-solid fa-flask';
	}
	if (keyLower.includes('preset') || labelLower.includes('preset')) {
		return 'fa-solid fa-sliders';
	}
	if (keyLower.includes('custom') || labelLower.includes('custom')) {
		return 'fa-solid fa-pen-to-square';
	}
	return 'fa-solid fa-bolt';
}

async function generateInitialValueForDataType(
	valueOwner: EntityWithDefaultValueState | null,
	dataType: DataTypeState | null,
	project: ProjectState,
	options?: { valueKey?: string }
): Promise<any> {
	if (!!(valueOwner as any)?.codeNativeInitExampleValue) {
		const resolvedValue = (
			await (valueOwner as InputMapState).codeNativeInitExampleValue(
				valueOwner,
				options
			)
		)?.value;

		if (resolvedValue !== undefined) {
			return resolvedValue;
		}
	}

	if (!dataType) return null;

	const oldFlatOrs = flattenDataTypesInOrGroup(dataType, {
		removeDuplicates: true,
		removeNulls: true,
		excludeSelf: true
	});

	const flatOrs = dataType.orChildrenGroup || [];

	if (dataType.isList) {
		const initializedValues: any[] = [];

		if (dataType.orChildrenGroup && dataType.orChildrenGroup.length > 0) {
			for (let i = 0; i < dataType.orChildrenGroup.length; i++) {
				const dt = dataType.orChildrenGroup[i];
				// if (flatOrs.length > 0) {
				// 	flatOrs.forEach((dt) => {

				const value = await generateInitialValueForDataType(
					null,
					dt,
					project
				);
				initializedValues.push(value);
			}
		}

		if (dataType.andChildrenGroup && dataType.andChildrenGroup.length > 0) {
			for (let i = 0; i < dataType.andChildrenGroup.length; i++) {
				const dt = dataType.andChildrenGroup[i];

				const value = generateInitialValueForDataType(
					null,
					dt,
					project
				);

				initializedValues.push(value);
			}
		}

		return initializedValues;
	}

	let effectiveDataType = dataType;

	if (flatOrs.length === 1) {
		effectiveDataType = flatOrs[0];
	} else if (!!flatOrs.length) {
		let mostUseful = flatOrs[0];

		// Try to avoid null and void
		while (
			!!mostUseful &&
			[PrimitiveTypes.Null, PrimitiveTypes.Void].includes(
				mostUseful.entity?.name as PrimitiveTypes
			)
		) {
			mostUseful =
				flatOrs.at(flatOrs.indexOf(mostUseful) + 1) || mostUseful;

			if (
				!!mostUseful.entity &&
				(mostUseful.entity.type !== EntityType.PrimitiveEntity ||
					![PrimitiveTypes.Null, PrimitiveTypes.Void].includes(
						mostUseful.entity?.name as PrimitiveTypes
					))
			) {
				break;
			}
		}

		effectiveDataType = mostUseful;
	}

	let flatAnd =
		flattenDataTypeAndGroup(effectiveDataType) || effectiveDataType;

	if (!flatAnd) {
		return null;
	}

	if (flatAnd.foreignKeyRef) {
		const refDataType = flatAnd.foreignKeyRef.getDataType(null);
		return generateInitialValueForDataType(null, refDataType, project);
	}

	if (flatAnd.entity?.type === EntityType.PrimitiveEntity) {
		switch (flatAnd.entity.name) {
			case PrimitiveTypes.String:
			case PrimitiveTypes.UUID:
			case PrimitiveTypes.FileReference:
				return 'Example value';
			case PrimitiveTypes.Number:
				return 1;
			case PrimitiveTypes.Boolean:
				return false;
			case PrimitiveTypes.Date:
				return new Date().toISOString();
			case PrimitiveTypes.KeyValue:
				return {};
			case PrimitiveTypes.Enum:
				return (flatAnd.options as string[])?.[0] ?? '';
			case PrimitiveTypes.Null:
				return null;
			default:
				return '';
		}
	}

	if (flatAnd.entity?.type === EntityType.DefinitionEntity) {
		const result: Record<string, any> = {};
		const defEntity = flatAnd.entity as DefinitionEntityState;

		if (flatAnd.asType) {
			return {
				id: '',
				name: '',
				type: EntityType.DefinitionEntity
			};
		}

		for (let i = 0; i < defEntity.properties.length; i++) {
			const prop = defEntity.properties[i];

			const propDataType = prop.getDataType(null);
			const entityName = resolveEntityName(prop, project);
			const codeName =
				prop.codeName ||
				(resolveEntityCodeName(prop, project) as string) ||
				lowercaseFirstLetter(toCamelCase(entityName));

			result[codeName] = await generateInitialValueForDataType(
				prop,
				propDataType,
				project
			);
		}

		return result;
	}

	if (flatAnd.entity?.type === EntityType.ActionDescriptor) {
		return {
			id: '',
			name: '',
			type: EntityType.ActionDescriptor
		};
	}

	return null;
}

const deducePrimitiveType = (
	literalValue?: LiteralValueState
): PrimitiveTypes | null => {
	if (!literalValue) return null;

	const name = literalValue.name;
	if (name && Object.values(PrimitiveTypes).includes(name)) {
		if (name === PrimitiveTypes.KeyValue) {
			return null;
		}
		return name;
	}

	const val = literalValue.value;
	if (typeof val === 'string') {
		return PrimitiveTypes.String;
	}
	if (typeof val === 'number') {
		return PrimitiveTypes.Number;
	}
	if (typeof val === 'boolean') {
		return PrimitiveTypes.Boolean;
	}
	if (val === null) {
		return PrimitiveTypes.Null;
	}
	return null;
};

const getPrimitiveNameOfDataType = (
	dt: DataTypeState
): PrimitiveTypes | null => {
	let effective = dt;
	if (dt.foreignKeyRef && !dt.entity) {
		const refDataType = dt.foreignKeyRef.getDataType?.(null);
		if (refDataType) {
			const refResolved = flattenDataTypesInOrGroup(
				flattenDataTypeAndGroup(refDataType),
				{ removeDuplicates: true, removeNulls: true }
			).filter((d) => !!d.isResolved);

			if (refResolved.length === 1) {
				effective = refResolved[0];
			}
		}
	}
	if (effective.entity?.type === EntityType.PrimitiveEntity) {
		return effective.entity.name as PrimitiveTypes;
	}
	return null;
};

const JsonEditorDialog: React.FC<IJsonEditorDialogProps> = (props) => {
	const onlyDefault = props.onlyDefault || false;
	Logger.log(
		'Selected on mount before: ',
		props.project.canvas?.selectedObjects
	);
	props.project.canvas?.unselectAll();
	Logger.log(
		'Selected on mount after: ',
		props.project.canvas?.selectedObjects
	);

	const valueOwnerCanvasParentEntity = getParentCanvasEntity(
		props.valueOwner
	);

	const resolveSubHeaderText = () => {
		let entityTypeDescription = '';

		const parentEntityName = resolveEntityName(
			valueOwnerCanvasParentEntity,
			props.valueOwner.project
		);

		const parentEntityDescriptor =
			props.valueOwner.id !== valueOwnerCanvasParentEntity.id
				? ' of ' +
					parentEntityName +
					' ' +
					lowercaseFirstLetter(
						READABLE_ENTITY_TYPES[valueOwnerCanvasParentEntity.type]
							.singular
					)
				: '';

		switch (props.valueOwner.type) {
			case EntityType.VariableDeclaration:
				entityTypeDescription = 'Variable';
				break;
			case EntityType.ArgumentDeclaration:
				entityTypeDescription = 'Input';
				break;
			case EntityType.InputMap: {
				entityTypeDescription = 'Input';

				if (
					TERMINATION_TYPES.includes(
						valueOwnerCanvasParentEntity.type
					)
				) {
					entityTypeDescription = 'Output';
				}
				break;
			}
			case EntityType.OutputMap:
				entityTypeDescription = 'Output';
				break;
		}

		return entityTypeDescription + parentEntityDescriptor;
	};

	const valueOwnerDataType = props.valueOwner.getDataType(null);
	const schema = toJSONSchema(valueOwnerDataType, resolveSubHeaderText());

	let initialValue = '';
	try {
		initialValue =
			props.value && JSON.stringify(props.value.value || null, null, 2);
	} catch (e) {
		// ignore
	}

	const [rawValue, setRawValue] = useState<string>(initialValue);
	const [editorMode, setEditorMode] = useState<'tree' | 'code'>(
		() => props.project?.userSettings?.jsonEditorMode || 'tree'
	);

	useEffect(() => {
		if (props.onChange) {
			try {
				props.onChange(JSON.parse(rawValue));
			} catch (e) {
				// Ignore parse errors
			}
		}
	}, [rawValue]);

	// ref of the editor
	const editorRef = useRef<JSONEditor>(null);
	const [editorErrors, setEditorErrors] = useState<
		ReadonlyArray<SchemaValidationError | ParseError>
	>([]);

	// === Example Values Options Dropdown =====================================
	const [exampleValueOptions, setExampleValueOptions] = useState<
		IExampleValuesOption[]
	>([]);
	const [exampleValueMenuAnchor, setExampleValueMenuAnchor] =
		useState<null | HTMLElement>(null);
	const isExampleValueMenuOpen = Boolean(exampleValueMenuAnchor);

	useEffect(() => {
		let isMounted = true;
		const fetchOptions = async () => {
			if (
				props.valueOwner &&
				typeof (props.valueOwner as any)
					.codeNativeGetExampleValuesOptions === 'function'
			) {
				try {
					const opts = await (
						props.valueOwner as any
					).codeNativeGetExampleValuesOptions(props.valueOwner);
					if (isMounted && Array.isArray(opts)) {
						setExampleValueOptions(opts);
					}
				} catch (err) {
					Logger.error('Failed to fetch example value options', err);
				}
			} else {
				setExampleValueOptions([]);
			}
		};
		fetchOptions();
		return () => {
			isMounted = false;
		};
	}, [props.valueOwner]);

	const visibleExampleOptions = useMemo(() => {
		return exampleValueOptions.filter((opt) => !opt.hidden);
	}, [exampleValueOptions]);

	const hasMultipleExampleOptions = visibleExampleOptions.length > 1;

	const handleOpenExampleValueMenu = (
		e: React.MouseEvent<HTMLButtonElement>
	) => {
		setExampleValueMenuAnchor(e.currentTarget);
	};

	const handleCloseExampleValueMenu = () => {
		setExampleValueMenuAnchor(null);
	};

	// === Cast-to dropdown ====================================================
	// For owners whose data-type is an `or`-group (e.g. `text | number`), we
	// surface a small dropdown that lets the user pick a concrete subtype to
	// edit as. The pick is local-only — it doesn't mutate the entity's
	// data-type — it just swaps the JSON editor for the matching primitive
	// input (like `DynamicValueEntry` does inline).
	const castOptions = useMemo<DataTypeState[]>(() => {
		const flat = flattenDataTypesInOrGroup(
			flattenDataTypeAndGroup(valueOwnerDataType),
			{ removeDuplicates: true, removeNulls: false }
		).filter((dt) => !!dt.isResolved);

		// Drop the trivial wrapping case where the owner is already a single
		// resolved type (the cast dropdown would offer that same type only).
		if (flat.length < 2) return [];

		return flat;
	}, [valueOwnerDataType]);

	const initialCastDataType = useMemo<DataTypeState | null>(() => {
		if (!props.value) return null;

		const deducedType = deducePrimitiveType(
			props.value as LiteralValueState
		);
		if (!deducedType) return null;

		return (
			castOptions.find((opt) => {
				return getPrimitiveNameOfDataType(opt) === deducedType;
			}) || null
		);
	}, [props.value, castOptions]);

	const [castDataType, setCastDataType] = useState<DataTypeState | null>(
		initialCastDataType
	);
	const [castedValue, setCastedValue] = useState<
		string | number | boolean | null
	>(null);

	const customValidationErrors = useMemo(() => {
		if (
			!props.valueOwner ||
			typeof (props.valueOwner as any).codeNativeValueValidation !==
				'function'
		) {
			return [];
		}

		let parsed: any;
		if (castDataType) {
			parsed = castedValue;
		} else {
			try {
				parsed = JSON.parse(rawValue);
			} catch (_) {
				return [];
			}
		}

		try {
			const res = (props.valueOwner as any).codeNativeValueValidation(
				props.valueOwner,
				parsed
			);
			return res?.errors || [];
		} catch (e) {
			return [];
		}
	}, [props.valueOwner, rawValue, castDataType, castedValue]);

	const renderCastOption = (option: DataTypeState) => {
		if (option.isList) {
			return (
				<span
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						padding: '4px 0'
					}}
				>
					<span
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 8,
							fontSize: '14px',
							fontWeight: 500
						}}
					>
						<ExplicitDataTypeIcon
							icon="list"
							size="small"
							id={option.id}
						/>
						{resolveCastOptionLabel(option)}
					</span>
					<p
						style={{
							margin: 0,
							marginLeft: 24,
							fontSize: '11px',
							color: 'rgba(0,0,0,0.54)'
						}}
					>
						List of values
					</p>
				</span>
			);
		}

		if (option.foreignKeyRef) {
			const foreignEntityName = resolveEntityName(
				option.foreignKeyRef,
				props.project.logic
			);
			const foreignKeyParentName = resolveEntityName(
				option.foreignKeyRef.parent,
				props.project.logic
			);
			const subLabel = `Reference to ${foreignEntityName} from ${foreignKeyParentName}`;

			return (
				<span
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
						padding: '4px 0'
					}}
				>
					<span
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 8,
							fontSize: '14px',
							fontWeight: 500
						}}
					>
						<DataTypeIcon
							entity={option.foreignKeyRef}
							size="small"
							project={props.project}
						/>
						{resolveEntityName(
							option.foreignKeyRef,
							props.project.logic
						) || '-'}
					</span>
					<p
						style={{
							margin: 0,
							marginLeft: 24,
							fontSize: '11px',
							color: 'rgba(0,0,0,0.54)'
						}}
					>
						{subLabel}
					</p>
				</span>
			);
		}

		if (option.entity) {
			if (option.entity.type === EntityType.PrimitiveEntity) {
				const primName = option.entity.name as PrimitiveTypes;
				return (
					<span
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							padding: '4px 0'
						}}
					>
						<span
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 8,
								color: fromLiteralValueTypeToFontColor(
									primName
								),
								fontSize: '14px',
								fontWeight: 500
							}}
						>
							<ExplicitDataTypeIcon
								icon={fromLiteralValueTypeToExplicitIconName(
									primName
								)}
								size="small"
								id={option.entity.id}
							/>
							{fromLiteralValueTypeToReadableLabel(primName)}
						</span>
						<p
							style={{
								margin: 0,
								marginLeft: 24,
								fontSize: '11px',
								color: 'rgba(0,0,0,0.54)'
							}}
						>
							{resolveLiteralValueSubheader(primName)}
						</p>
					</span>
				);
			}

			if (
				[
					EntityType.ActionDescriptor,
					EntityType.DefinitionEntity,
					EntityType.BuiltInBaseEntity
				].includes(option.entity.type as EntityType)
			) {
				return (
					<span
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							padding: '4px 0'
						}}
					>
						<span
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 8,
								fontSize: '14px',
								fontWeight: 500
							}}
						>
							<DefinitionEntityIcon
								entity={option.entity as DefinitionEntityState}
								size="small"
							/>
							{resolveEntityName(
								option.entity,
								props.project.logic
							) || '-'}
						</span>
						<p
							style={{
								margin: 0,
								marginLeft: 24,
								fontSize: '11px',
								color: 'rgba(0,0,0,0.54)'
							}}
						>
							{resolveCardSubheader(
								option.entity as DefinitionEntityState
							) || 'Definition entity'}
						</p>
					</span>
				);
			}
		}

		return (
			<span style={{ fontSize: '14px', fontWeight: 500 }}>
				{resolveCastOptionLabel(option)}
			</span>
		);
	};

	const renderSelectedCastOption = (option: DataTypeState) => {
		if (option.isList) {
			return (
				<span
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 8,
						fontSize: '14px',
						fontWeight: 500
					}}
				>
					<ExplicitDataTypeIcon
						icon="list"
						size="small"
						id={option.id}
					/>
					{resolveCastOptionLabel(option)}
				</span>
			);
		}

		if (option.foreignKeyRef) {
			return (
				<span
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 8,
						fontSize: '14px',
						fontWeight: 500
					}}
				>
					<DataTypeIcon
						entity={option.foreignKeyRef}
						size="small"
						project={props.project}
					/>
					{resolveEntityName(
						option.foreignKeyRef,
						props.project.logic
					) || '-'}
				</span>
			);
		}

		if (option.entity) {
			if (option.entity.type === EntityType.PrimitiveEntity) {
				const primName = option.entity.name as PrimitiveTypes;
				return (
					<span
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 8,
							color: fromLiteralValueTypeToFontColor(primName),
							fontSize: '14px',
							fontWeight: 500
						}}
					>
						<ExplicitDataTypeIcon
							icon={fromLiteralValueTypeToExplicitIconName(
								primName
							)}
							size="small"
							id={option.entity.id}
						/>
						{fromLiteralValueTypeToReadableLabel(primName)}
					</span>
				);
			}

			if (
				[
					EntityType.ActionDescriptor,
					EntityType.DefinitionEntity,
					EntityType.BuiltInBaseEntity
				].includes(option.entity.type as EntityType)
			) {
				return (
					<span
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 8,
							fontSize: '14px',
							fontWeight: 500
						}}
					>
						<DefinitionEntityIcon
							entity={option.entity as DefinitionEntityState}
							size="small"
						/>
						{resolveEntityName(
							option.entity,
							props.project.logic
						) || '-'}
					</span>
				);
			}
		}

		return (
			<span style={{ fontSize: '14px', fontWeight: 500 }}>
				{resolveCastOptionLabel(option)}
			</span>
		);
	};

	// When the cast type changes, seed the local edited value from the current
	// raw JSON, falling back to a sensible default (false / '' / null) when
	// the existing value can't be coerced into the chosen primitive.
	useEffect(() => {
		if (!castDataType) return;

		let parsed: unknown = null;
		try {
			parsed = JSON.parse(rawValue);
		} catch (_) {
			parsed = null;
		}

		const primitiveName =
			castDataType.entity?.type === EntityType.PrimitiveEntity
				? (castDataType.entity.name as PrimitiveTypes)
				: null;

		const isCompatibleType = (() => {
			switch (primitiveName) {
				case PrimitiveTypes.Boolean:
					return typeof parsed === 'boolean';
				case PrimitiveTypes.Number:
					return typeof parsed === 'number';
				case PrimitiveTypes.Null:
					return parsed === null;
				case PrimitiveTypes.String:
				case PrimitiveTypes.UUID:
				case PrimitiveTypes.Date:
				case PrimitiveTypes.Enum:
					return typeof parsed === 'string';
				default:
					return false;
			}
		})();

		if (isCompatibleType) {
			setCastedValue(parsed as string | number | boolean | null);
		} else {
			switch (primitiveName) {
				case PrimitiveTypes.Boolean:
					setCastedValue(false);
					break;
				case PrimitiveTypes.Number:
					setCastedValue(0);
					break;
				case PrimitiveTypes.Null:
					setCastedValue(null);
					break;
				default:
					setCastedValue('');
			}
		}
	}, [castDataType]);

	const resolveCastOptionLabel = (dt: DataTypeState): string => {
		const label = resolveDataTypeLabel(dt);
		return label || 'Unspecified data type';
	};

	const renderCastInput = () => {
		if (!castDataType) return null;

		// Same trick as `DynamicValueEntry`: if the cast target is a foreign-key
		// reference, render the input that matches the referenced property's
		// primitive entity (a UUID input, a number input, …).
		let effectiveDataType: DataTypeState = castDataType;
		if (castDataType.foreignKeyRef && !castDataType.entity) {
			const refDataType = castDataType.foreignKeyRef.getDataType?.(null);
			if (refDataType) {
				const refResolved = flattenDataTypesInOrGroup(
					flattenDataTypeAndGroup(refDataType),
					{ removeDuplicates: true, removeNulls: true }
				).filter((dt) => !!dt.isResolved);

				if (refResolved.length === 1) {
					effectiveDataType = refResolved[0];
				}
			}
		}

		const primitiveName =
			effectiveDataType.entity?.type === EntityType.PrimitiveEntity
				? (effectiveDataType.entity.name as PrimitiveTypes)
				: null;

		if (primitiveName === PrimitiveTypes.Boolean) {
			return (
				<CheckboxWithLabel
					mainLabel="Boolean value"
					subLabel={castedValue ? 'True' : 'False'}
					checked={castedValue === true}
					onChange={(checked: boolean | null) =>
						setCastedValue(checked as boolean)
					}
				/>
			);
		}

		if (primitiveName === PrimitiveTypes.Null) {
			return (
				<p style={{ margin: 0, color: 'rgba(0,0,0,0.54)' }}>
					This value will be saved as <code>null</code>.
				</p>
			);
		}

		if (
			primitiveName === PrimitiveTypes.Enum &&
			Array.isArray(effectiveDataType.options) &&
			effectiveDataType.options.length
		) {
			return (
				<FormControl fullWidth>
					<InputLabel id="cast-enum-label">Value</InputLabel>
					<Select
						size="small"
						labelId="cast-enum-label"
						label="Value"
						value={(castedValue as string) || ''}
						onChange={(e) =>
							setCastedValue(e.target.value as string)
						}
					>
						{effectiveDataType.options.map((option) => (
							<MenuItem key={option} value={option}>
								{option}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			);
		}

		return (
			<FormControl fullWidth>
				<InputLabel htmlFor="cast-input">Value</InputLabel>
				<OutlinedInput
					id="cast-input"
					fullWidth
					autoFocus
					size="small"
					label="Value"
					type={resolveInputTypeFromDataType(effectiveDataType)}
					value={castedValue ?? ''}
					onChange={(e) => {
						const sanitized = castHTMLInputValueToType(
							e.target.value,
							effectiveDataType
						);
						setCastedValue(
							sanitized as string | number | boolean | null
						);
					}}
				/>
			</FormControl>
		);
	};

	const handleOnNodeName = useCallback(
		(nodeNameParams: any) => {
			Logger.log('onNodeName', nodeNameParams);
			const valueAsKnownValueAsType: any = nodeNameParams.value;

			if (
				!!valueAsKnownValueAsType &&
				!!valueAsKnownValueAsType.type &&
				[
					EntityType.DefinitionEntity,
					EntityType.FunctionDeclaration,
					EntityType.Operation,
					EntityType.FunctionCall,
					EntityType.ActionDescriptor,
					EntityType.BuiltInBaseEntity
				].includes(valueAsKnownValueAsType.type)
			) {
				// Note: We need to be careful with dependencies here.
				// If props.project.logic changes, this function updates.
				// The "Ref Proxy" in RawJsonEditor handles the update gracefully.
				const resolvedEntityName = resolveEntityName(
					valueAsKnownValueAsType,
					props.project.logic
				);
				if (!!resolvedEntityName) {
					return 'Template of ' + resolvedEntityName;
				} else {
					return 'Template';
				}
			}
			return undefined;
		},
		[props.project.logic]
	); // Dependency ensures it updates if logic changes

	const handleOnChangeText = useCallback((value: string) => {
		Logger.log('onChangeText', value);
		setRawValue(value);
	}, []);

	// Based on the entity type, we resolve the header text
	const resolveHeaderText = () => {
		return resolveEntityName(props.valueOwner, props.valueOwner.project);
	};

	const isStrictObjectStructure =
		valueOwnerDataType?.hasStrictObjectStructure();
	const defaultValue = isStrictObjectStructure
		? (
				valueOwnerDataType.entity as DefinitionEntityState
			).getRawDefaultValue()
		: null;

	const isObjectStructure = valueOwnerDataType?.isObject();

	const isUntyped =
		!valueOwnerDataType ||
		(!valueOwnerDataType?.entity &&
			!valueOwnerDataType.foreignKeyRef &&
			!valueOwnerDataType.isList &&
			!valueOwnerDataType.orChildrenGroup.length &&
			!valueOwnerDataType.andChildrenGroup.length) ||
		valueOwnerDataType?.entity?.name === PrimitiveTypes.Untyped;

	const hasStrictObjectStructure =
		valueOwnerDataType?.hasStrictObjectStructure();

	const hasInlineDeclaredObjectDataTypeEntity =
		!!valueOwnerDataType?.entity &&
		valueOwnerDataType?.entity.type === EntityType.DefinitionEntity &&
		valueOwnerDataType?.entity.parent?.id === valueOwnerDataType?.id;

	const canInlineDeclaredObjectDataTypeEntityBeExplicit =
		hasInlineDeclaredObjectDataTypeEntity &&
		![EntityType.Search, EntityType.Loop].includes(
			props.valueOwner.parent?.type
		);

	const keys =
		!!props.value?.value &&
		isObject(props.value?.value) &&
		Object.keys(props.value?.value);

	const existingGloballyDeclaredDefinitionEntity =
		!!props.value?.value &&
		isObject(props.value?.value) &&
		findEntityFromKeys(keys, props.project.logic);

	const showGoToDefinitionButton = !!existingGloballyDeclaredDefinitionEntity;

	const showMakeDefinitionExplicitButton =
		!existingGloballyDeclaredDefinitionEntity &&
		canInlineDeclaredObjectDataTypeEntityBeExplicit &&
		hasInlineDeclaredObjectDataTypeEntity;

	const showCreateDefinitionEntityButton =
		!showMakeDefinitionExplicitButton &&
		(!valueOwnerDataType ||
			(isObjectStructure && !hasStrictObjectStructure) ||
			(isUntyped && isObject(props.value?.value)));

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				gap: '4px',
				padding: props.embedded ? '0' : '40px'
			}}
		>
			{/* Header */}
			{!props.embedded && (
				<span>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '16px'
						}}
					>
						<button
							className="dialog_back_button"
							onClick={(e) => {
								props.onClose?.(e);
							}}
						>
							<i className="fa-solid fa-arrow-left"></i>
						</button>
						<h1
							style={{
								margin: '0px',
								fontWeight: 500,
								fontSize: '28px'
							}}
						>
							{resolveHeaderText()}
						</h1>
					</div>
					<span
						style={{
							display: 'flex',
							gap: '6px',
							marginLeft: '42px'
						}}
					>
						<p style={{ margin: '0px', color: 'rgba(0,0,0,0.7)' }}>
							{resolveSubHeaderText()}
						</p>
					</span>
					<div
						style={{
							marginLeft: '42px',
							marginTop: '16px',
							backgroundColor: 'rgba(0,0,0,0.03)',
							padding: '16px',
							borderRadius: '8px',
							maxWidth: '800px'
						}}
					>
						<p
							style={{
								margin: 0,
								fontSize: '14px',
								color: 'rgba(0,0,0,0.7)'
							}}
						>
							<strong>Property code names:</strong> Properties
							defined in Elyx are automatically camel-cased for
							JSON code/data structures. For example,{' '}
							<em>"User's first name"</em> becomes{' '}
							<code style={{ fontFamily: 'monospace' }}>
								usersFirstName
							</code>
							.
						</p>

						{hasStrictObjectStructure &&
							valueOwnerDataType?.entity?.type ===
								EntityType.DefinitionEntity && (
								<div style={{ marginTop: '12px' }}>
									<p
										style={{
											margin: '0 0 8px 0',
											fontSize: '14px',
											color: 'rgba(0,0,0,0.7)'
										}}
									>
										<strong>
											Expected keys for this object:
										</strong>
									</p>
									<table
										style={{
											width: '100%',
											borderCollapse: 'collapse',
											fontSize: '13px',
											backgroundColor: 'white',
											border: '1px solid rgba(0,0,0,0.1)'
										}}
									>
										<thead>
											<tr
												style={{
													backgroundColor:
														'rgba(0,0,0,0.02)',
													textAlign: 'left',
													borderBottom:
														'1px solid rgba(0,0,0,0.1)'
												}}
											>
												<th
													style={{
														padding: '6px 12px',
														borderRight:
															'1px solid rgba(0,0,0,0.1)'
													}}
												>
													Original Name
												</th>
												<th
													style={{
														padding: '6px 12px'
													}}
												>
													Code Name
												</th>
											</tr>
										</thead>
										<tbody>
											{(
												valueOwnerDataType.entity as DefinitionEntityState
											).properties.map((prop) => {
												const entityName =
													resolveEntityName(
														prop,
														props.project.logic
													);
												const codeName =
													prop.codeName ||
													(resolveEntityCodeName(
														prop,
														props.project.logic
													) as string) ||
													lowercaseFirstLetter(
														toCamelCase(entityName)
													);
												return (
													<tr
														key={prop.id}
														style={{
															borderBottom:
																'1px solid rgba(0,0,0,0.1)'
														}}
													>
														<td
															style={{
																padding:
																	'6px 12px',
																borderRight:
																	'1px solid rgba(0,0,0,0.1)',
																color: 'rgba(0,0,0,0.7)'
															}}
														>
															{entityName}
														</td>
														<td
															style={{
																padding:
																	'6px 12px'
															}}
														>
															<code
																style={{
																	backgroundColor:
																		'rgba(0,0,0,0.04)',
																	border: '1px solid rgba(0,0,0,0.1)',
																	borderRadius:
																		'4px',
																	padding:
																		'2px 4px',
																	color: '#d63384'
																}}
															>
																{codeName}
															</code>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							)}
					</div>
				</span>
			)}

			<div
				style={{
					flex: 1,
					margin: '16px 0',
					// margin: '16px 42px',
					overflow: 'scroll'
				}}
			>
				<section
					style={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%'
					}}
				>
					<div
						style={{
							margin: props.embedded
								? '0 16px 16px'
								: '0 42px 16px',
							paddingTop: '12px',
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'flex-start',
							gap: '16px',
							flexWrap: 'wrap'
						}}
					>
						{/* Left Side: Title & Data Type Info */}
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '4px',
								minWidth: 0,
								flex: 1
							}}
						>
							<h3
								style={{
									display: 'flex',
									gap: '6px',
									fontWeight: 500,
									fontSize: '18px',
									margin: 0,
									color: 'rgba(0, 0, 0, 0.87)'
								}}
							>
								Value
							</h3>
							<p
								style={{
									margin: 0,
									fontSize: '13px',
									lineHeight: '1.4',
									color: 'rgba(0, 0, 0, 0.54)',
									wordBreak: 'break-word'
								}}
							>
								{resolveEntityDataTypeLabel(props.valueOwner)}
							</p>
						</div>

						{/* Right Side: Action Controls Bar */}
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '10px',
								flexShrink: 0
							}}
						>
							<InitializeValueButton
								valueOwner={props.valueOwner}
								project={props.project}
								onInitializeValue={(val) =>
									setRawValue(JSON.stringify(val, null, 2))
								}
							/>

							{castOptions.length > 0 && (
								<FormControl
									size="small"
									sx={{ minWidth: 160, flexShrink: 0 }}
								>
									<InputLabel id="cast-data-type-label">
										Edit as
									</InputLabel>
									<Select
										size="small"
										labelId="cast-data-type-label"
										label="Edit as"
										value={castDataType?.id || ''}
										renderValue={(value) => {
											if (!value) {
												return <em>Raw JSON</em>;
											}
											const picked = castOptions.find(
												(opt) => opt.id === value
											);
											if (!picked)
												return <em>Raw JSON</em>;
											return renderSelectedCastOption(
												picked
											);
										}}
										onChange={(e) => {
											const id = e.target.value as string;
											if (!id) {
												setCastDataType(null);
												return;
											}
											const picked = castOptions.find(
												(opt) => opt.id === id
											);
											setCastDataType(picked || null);
										}}
									>
										<MenuItem value="">
											<em>Raw JSON</em>
										</MenuItem>
										{castOptions.map((option) => (
											<MenuItem
												key={option.id}
												value={option.id}
											>
												{renderCastOption(option)}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							)}

							<ToggleButtonGroup
								size="small"
								value={editorMode}
								exclusive
								onChange={(
									e,
									newMode: 'tree' | 'code' | null
								) => {
									if (newMode !== null) {
										setEditorMode(newMode);
										props.project?.userSettings?.set(
											'jsonEditorMode',
											newMode
										);
										if (editorRef.current) {
											editorRef.current.setMode(newMode);
										}
									}
								}}
								aria-label="editor mode"
								sx={{ flexShrink: 0 }}
							>
								<ToggleButton
									value="tree"
									aria-label="tree mode"
								>
									<i className="fa-solid fa-folder-tree"></i>
								</ToggleButton>
								<ToggleButton
									value="code"
									aria-label="code mode"
								>
									<i className="fa-solid fa-code"></i>
								</ToggleButton>
							</ToggleButtonGroup>

							<IconButton
								style={{ width: 32, height: 32 }}
								onClick={() => {
									Logger.log('clicked');
								}}
								size="small"
								id="json-editor-options-menu"
							>
								<i className="fa-solid fa-bars"></i>
							</IconButton>

							{/* Menu dropdown */}
							<DropdownMenuPopup
								popupOptions={{
									anchorQuerySelector:
										'#json-editor-options-menu',
									placement: 'bottom-end'
								}}
								options={[
									{
										label: 'Format',
										iconStartSrc: FormatIconBlack,
										iconStartSrcHover: FormatIconWhite,
										iconStartClasses:
											'json-editor-dialog-format-code-icons',
										onClick: () => {
											try {
												const parsed =
													JSON.parse(rawValue);
												setRawValue(
													JSON.stringify(
														parsed,
														null,
														2
													)
												);
											} catch (e) {
												Logger.log('Invalid JSON');
												popupNotification({
													text: 'Invalid JSON',
													color: 'error'
												});
											}
										}
									},
									{
										label: 'Single line',
										iconStartSrc: InlineIconBlack,
										iconStartSrcHover: InlineIconWhite,
										iconStartClasses:
											'json-editor-dialog-format-code-icons',
										onClick: () => {
											try {
												const parsed =
													JSON.parse(rawValue);
												setRawValue(
													JSON.stringify(parsed)
												);
											} catch (e) {
												Logger.log('Invalid JSON');
												popupNotification({
													text: 'Invalid JSON',
													color: 'error'
												});
											}
										}
									},
									{
										label: 'Copy',
										iconStart: 'fa-solid fa-clipboard',
										onClick: () => {
											navigator.clipboard.writeText(
												rawValue
											);
											popupNotification({
												text: 'Copied to clipboard'
											});
										}
									},
									{
										label: 'Paste',
										iconStart: 'fa-solid fa-paste',
										onClick: () => {
											navigator.clipboard
												.readText()
												.then((text) => {
													Logger.log(
														'Pasted text',
														text
													);
													setRawValue(text);
												})
												.catch((err) => {
													Logger.error(
														'Failed to read clipboard',
														err
													);
													popupNotification({
														text: 'Failed to read clipboard',
														color: 'error'
													});
												});
										}
									}
								]}
							/>
						</div>
					</div>

					{castDataType ? (
						<div
							style={{
								margin: props.embedded ? '0 16px' : '0 42px',
								marginTop: 6,
								display: 'flex',
								flexDirection: 'column',
								gap: 12
							}}
						>
							{renderCastInput()}
						</div>
					) : (
						<RawJsonEditor
							schema={schema}
							text={rawValue}
							mode={editorMode}
							onExpand={(expandParams: ExpandOptions) => {
								Logger.log('onExpand', expandParams);
							}}
							onNodeName={handleOnNodeName}
							onReady={(editor: JSONEditor) => {
								editorRef.current = editor;
								editor.setMode(editorMode);
							}}
							onChangeText={handleOnChangeText}
							onValidationError={(
								errors: ReadonlyArray<
									SchemaValidationError | ParseError
								>
							) => {
								Logger.log(errors);
								setEditorErrors(errors);
							}}
						/>
					)}

					{customValidationErrors.length > 0 && (
						<div
							style={{
								margin: props.embedded
									? '12px 16px 0'
									: '12px 42px 0',
								padding: '12px 16px',
								backgroundColor: '#fdeded',
								border: '1px solid #ef5350',
								borderRadius: '6px',
								display: 'flex',
								flexDirection: 'column',
								gap: '4px'
							}}
						>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									color: '#d32f2f',
									fontWeight: 600,
									fontSize: '13px'
								}}
							>
								<i className="fa-solid fa-triangle-exclamation"></i>
								<span>
									Validation Error
									{customValidationErrors.length > 1
										? 's'
										: ''}
								</span>
							</div>
							{customValidationErrors.map(
								(err: any, idx: number) => (
									<p
										key={idx}
										style={{
											margin: 0,
											fontSize: '13px',
											color: '#d32f2f',
											lineHeight: '1.4'
										}}
									>
										{err.message || String(err)}
									</p>
								)
							)}
						</div>
					)}
				</section>
			</div>

			{!props.embedded && (
				<section
					style={{
						alignSelf: 'flex-start',
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'flex-end',
						width: '100%',
						gap: '16px',
						padding: '0 42px 24px'
					}}
				>
					<Button
						onClick={(e: any) => {
							props.onClose?.(e);
						}}
						variant="text"
					>
						Back
					</Button>
					{showCreateDefinitionEntityButton && (
						<Button
							variant="outlined"
							onClick={async () => {
								const changeSet =
									props.project.logic.addChangeSet(
										new ChangeSet(
											props.project.logic,
											ProjectState.sessionAuthor,
											new Date().toISOString(),
											props.project.logic,
											false,
											StateMutationAction.CreateDefFromValue
										)
									);

								const result =
									createDefinitionEntityFromJSONObject(
										props.value?.value as {
											[key: string]: any;
										},
										props.project.logic,
										{
											name:
												resolveEntityName(
													props.valueOwner,
													props.project.logic
												) + ' definition'
										},
										changeSet
									);

								const resolvedDefinitionEntity =
									result.changeSet.self;

								if (result.hasCreated) {
									let newX = valueOwnerCanvasParentEntity.x;
									const newY =
										valueOwnerCanvasParentEntity.y -
										calculateCardSize(
											resolvedDefinitionEntity
										).height -
										CANVAS_VERTICAL_BUFFER_BETWEEN_CARDS;

									const newCanvasEntities =
										entitiesToUniqueCanvasEntities(
											result.changeSet.added
										);

									// Give them a valid x and y coordinates
									newCanvasEntities.forEach((entity) => {
										newX += CANVAS_CARD_WIDTH * 2;

										// @ts-ignore
										entity.metaSync(
											{
												x: newX,
												y: newY
											},
											changeSet
										);
									});

									const objectId =
										getCanvasEntityDraggableContainerDOMId(
											resolvedDefinitionEntity
										);

									props.project.canvas?.scrollToObjectById(
										objectId,
										() => {
											props.onClose?.(null);
										}
									);
								} else {
									// Create notification that the entity already exists
									// Then scroll to the entity
									popupNotification({
										text: 'Compatible entity already exists',
										color: 'warning'
									});

									const canvasEntity = getParentCanvasEntity(
										resolvedDefinitionEntity
									);

									const objectId =
										getCanvasEntityDraggableContainerDOMId(
											canvasEntity
										);

									props.project.canvas?.scrollToObjectById(
										objectId,
										() => {
											props.onClose?.(null);
										}
									);
								}

								props.project.renderAndCloseChangeSet(
									changeSet
								);
							}}
						>
							Create entity definition
						</Button>
					)}
					{showMakeDefinitionExplicitButton && (
						<Button
							variant="outlined"
							onClick={async () => {
								const changeSet =
									props.project.logic.addChangeSet(
										new ChangeSet(
											props.project.logic,
											ProjectState.sessionAuthor,
											new Date().toISOString(),
											props.project.logic,
											false,
											StateMutationAction.MoveInlineDefToCanvas
										)
									);

								const existingInlineEntity =
									valueOwnerDataType?.entity as DefinitionEntityState;

								existingInlineEntity.setParent(
									props.project.logic,
									changeSet
								);
								valueOwnerDataType.metaSync(
									{
										inferred: false
									},
									changeSet
								);

								// if (result.hasCreated) {
								let newX = valueOwnerCanvasParentEntity.x;
								const newY =
									valueOwnerCanvasParentEntity.y -
									calculateCardSize(existingInlineEntity)
										.height -
									CANVAS_VERTICAL_BUFFER_BETWEEN_CARDS;

								const newCanvasEntities =
									entitiesToUniqueCanvasEntities([
										existingInlineEntity
									]);

								// Give them a valid x and y coordinates
								newCanvasEntities.forEach((entity) => {
									newX += CANVAS_CARD_WIDTH * 2;

									// @ts-ignore
									entity.metaSync(
										{
											x: newX,
											y: newY
										},
										changeSet
									);
								});

								const objectId =
									getCanvasEntityDraggableContainerDOMId(
										existingInlineEntity
									);

								props.project.canvas?.scrollToObjectById(
									objectId,
									() => {
										props.onClose?.(null);
									}
								);

								props.project.renderAndCloseChangeSet(
									changeSet
								);
							}}
						>
							Create entity definition
						</Button>
					)}
					{showGoToDefinitionButton && (
						<Button
							variant="outlined"
							onClick={(e: any) => {
								const canvasEntity = getParentCanvasEntity(
									valueOwnerDataType.entity
								);

								const objectId =
									getCanvasEntityDraggableContainerDOMId(
										canvasEntity
									);

								props.project.canvas?.scrollToObjectById(
									objectId,
									() => {
										props.onClose?.(e);
									}
								);
							}}
						>
							Go to definition
						</Button>
					)}
					<Button
						// Disabled if there are syntax/schema errors or custom value validation errors
						disabled={
							(!castDataType && !!editorErrors.length) ||
							!!customValidationErrors.length
						}
						onClick={(e: any) => {
							let valueToSave: unknown;

							if (castDataType) {
								valueToSave = castedValue;
							} else {
								Logger.log('Save value', rawValue);
								try {
									valueToSave = JSON.parse(rawValue);
								} catch (_) {
									valueToSave = null;
								}
							}

							let storage: ChangeSet | IValueStoreClient =
								props.project.localTestValues;

							if (onlyDefault) {
								storage = props.project.logic.addChangeSet(
									new ChangeSet(
										props.project.logic,
										ProjectState.sessionAuthor,
										new Date().toISOString(),
										props.valueOwner,
										false,
										StateMutationAction.SetPrimitiveDefaultValue
									)
								);
							}

							const asCastedType = castDataType
								? getPrimitiveNameOfDataType(castDataType) ||
									undefined
								: undefined;

							const changeSet = handleLiteralValueChange(
								valueToSave as any,
								props.valueOwner,
								props.project.logic,
								storage,
								{
									asCastedType
								}
							);

							if (!!changeSet) {
								props.project.renderAndCloseChangeSet(
									changeSet
								);
							}

							// Update the owner of the value in the canvas
							props.project.emit(
								valueOwnerCanvasParentEntity.id,
								{}
							);

							// If there is a test ongoing, notify the test
							props.project.test?.validate();

							// Close the dialog
							props.onClose?.(e);
						}}
						variant="contained"
						color="primary"
					>
						Save value
					</Button>
				</section>
			)}
		</div>
	);
};

export default JsonEditorDialog;
